import React from 'react';
import { RoutesPaths, useHistory } from '@shared/core';
import { createContext } from '@shared/utils/createContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type RouteNames = 'customize' | 'orderHistory' | 'orderDetails';

export const routePaths: RoutesPaths<RouteNames> = {
  customize: {
    path: 'customize/:cardDraftId',
    goToPath: (cardDraftId: string) => `customize/${cardDraftId}`
  },
  orderHistory: {
    path: 'order-history',
    goToPath: () => 'order-history'
  },
  orderDetails: {
    path: 'order/:printOrderNumber',
    goToPath: (printOrderNumber: string) => `order/${printOrderNumber}`
  }
};

type CardsRouterContext = {
  getCustomizeDraftPath: (cardDraftId: string) => string;
  getToOrderDetailsPath: (printOrderNumber: string) => string;
  goToOrderDetails: (printJobId: string) => void;
  goToCustomizeDraft: (draftId: string) => void;
  goToAdminDashboard: () => void;
};

const [Provider, useCardsRouterContext] = createContext<CardsRouterContext>({ name: 'CardsRouteProvider' });

const CardsRoutesProvider = ({ children, eventHandle }: { eventHandle: string; children: React.ReactNode }) => {
  const history = useHistory();

  const getToOrderDetailsPath = useEventCallback<CardsRouterContext['getToOrderDetailsPath']>(printOrderNumber => {
    return `/${eventHandle}/edit/card/order/${printOrderNumber}`;
  });

  const getCustomizeDraftPath = useEventCallback<CardsRouterContext['getCustomizeDraftPath']>(cardDraftId => {
    return `/${eventHandle}/edit/card/customize/${cardDraftId}`;
  });

  const goToOrderDetails = useEventCallback<CardsRouterContext['goToOrderDetails']>(printOrderId => {
    history.push(`/${eventHandle}/edit/card/order/${printOrderId}`);
  });

  const goToCustomizeDraft = useEventCallback((stationeryDraftId: string) => {
    history.push(getCustomizeDraftPath(stationeryDraftId));
  });

  const goToAdminDashboard = useEventCallback(() => {
    history.push(`/${eventHandle}/edit`);
  });

  return <Provider value={{ goToAdminDashboard, goToCustomizeDraft, getCustomizeDraftPath, getToOrderDetailsPath, goToOrderDetails }}>{children} </Provider>;
};

export { CardsRoutesProvider, useCardsRouterContext };
